import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertTriangle, Share2, FileText, Heart, Users, ExternalLink, Mail, MessageCircle, Instagram, Twitter as X, Facebook } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import NewsBanner from '../components/NewsBanner';
import { useNavigate } from 'react-router-dom';

const Action = () => {
  const [email, setEmail] = useState('');
  const [signedPetition, setSignedPetition] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleBannerImageClick = (link) => {
    console.log('Navigating to:', link);
    navigate(link);
  };

  const handleNewsletterSignup = (e) => {
    e.preventDefault();
    if (!email) return;
    
    toast({
      title: "Thank you for joining!",
      description: "You'll receive updates about vaquita conservation efforts and ways to help.",
      duration: 5000,
    });
    setEmail('');
  };

  const handlePetitionSign = () => {
    setSignedPetition(true);
    toast({
      title: "Petition Signed!",
      description: "Your voice has been added to the call for immediate vaquita protection.",
      duration: 5000,
    });
  };

  const shareContent = {
    text: "Fewer than 10 vaquitas remain! Help save the world's most endangered marine mammal from extinction.",
    url: window.location.href  // Use current page URL instead of just origin
  };

  const handleShare = (platform) => {
    const { text, url } = shareContent;
    let shareUrl = '';
    
    switch (platform) {
      case 'x':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      case 'instagram':
        toast({
          title: "Share on Instagram",
          description: "Copy this link and share it in your Instagram story or bio!",
          duration: 8000,
        });
        navigator.clipboard.writeText(url);
        return;
      case 'native':
        // Use Web Share API if available
        if (navigator.share) {
          navigator.share({
            title: 'Fight for Vaquita',
            text: text,
            url: url
          }).catch(console.error);
          return;
        } else {
          // Fallback: copy to clipboard
          navigator.clipboard.writeText(url).then(() => {
            toast({
              title: "Link Copied!",
              description: "Page URL copied to clipboard. Share it anywhere!",
              duration: 5000,
            });
          }).catch(() => {
            toast({
              title: "Share Link",
              description: url,
              duration: 10000,
            });
          });
          return;
        }
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="pt-20 bg-gray-900">
      {/* News Banner */}
      <NewsBanner onImageClick={handleBannerImageClick} />

      {/* Critical Alert Banner */}
      <div className="bg-red-800 text-white py-3 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center text-center text-sm sm:text-base">
            <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
            <p className="font-semibold">
              CRITICAL: Fewer than 10 vaquitas remain. Extinction expected within months without immediate action.
            </p>
          </div>
        </div>
      </div>

      {/* Hero Section with Marine Background */}
      <section className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
        {/* Marine Life Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1531306252771-c9544924d8e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtYXJpbmUlMjBvY2VhbnxlbnwwfHx8fDE3NTQxNzc2Mjh8MA&ixlib=rb-4.1.0&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/80 to-gray-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              Ways to Make a
              <span className="block text-green-400">Difference</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-4xl mx-auto">
              Every action counts when fewer than 10 vaquitas remain. Your voice, your sharing, your support 
              can help create the global pressure needed to save them.
            </p>
          </div>

          {/* Quick Action Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Share the Crisis */}
            <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Share2 className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-blue-300 mb-4">Share the Crisis</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Raise awareness by sharing the vaquita's story on social media. Every share reaches new people who can help.
              </p>
              
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => handleShare('x')}
                  className="w-full bg-black hover:bg-gray-900 text-white"
                >
                  <X className="h-4 w-4 mr-2" />
                  Share on X
                </Button>
                <Button 
                  onClick={() => handleShare('facebook')}
                  className="w-full bg-blue-800 hover:bg-blue-900 text-white"
                >
                  <Facebook className="h-4 w-4 mr-2" />
                  Share on Facebook
                </Button>
                <Button 
                  onClick={() => handleShare('instagram')}
                  className="w-full bg-pink-600 hover:bg-pink-700 text-white"
                >
                  <Instagram className="h-4 w-4 mr-2" />
                  Share on Instagram
                </Button>
              </div>
            </div>

            {/* Sign Petitions */}
            <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-green-300 mb-4">Sign Petitions</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Add your voice to international calls for immediate vaquita protection and gillnet bans.
              </p>
              
              <div className="space-y-3">
                <a 
                  href="https://www.change.org/search?q=vaquita"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Sign Vaquita Petitions
                  </Button>
                </a>
              </div>
            </div>

            {/* Support Organizations */}
            <div className="bg-purple-900/30 border border-purple-500/50 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-purple-300 mb-4">Support Scientists</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Donate to organizations working directly on vaquita conservation and research.
              </p>
              
              <div className="space-y-3">
                <a 
                  href="https://vaquitacpr.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Vaquita CPR
                  </Button>
                </a>
                <a 
                  href="https://porpoise.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full border border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white bg-transparent">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Porpoise Conservation
                  </Button>
                </a>
                <a 
                  href="https://www.worldwildlife.org/species/vaquita"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full border border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white bg-transparent">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    WWF Vaquita
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VaquitaCPR Scientific Solutions */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Scientific Conservation Solutions
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Based on VaquitaCPR's Project Esperanza and international scientific recommendations, 
              these evidence-based solutions are actively working to save vaquitas from extinction.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Stop the Demand */}
            <div className="bg-gradient-to-br from-red-900/30 to-red-800/20 border border-red-600/30 rounded-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mr-4">
                  <AlertTriangle className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-red-300">Stop the Demand</h3>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                Earth League International's "Operation Fake Gold" targets the illegal totoaba trade through 
                intelligence gathering across Mexico, China, and the US. They provide critical information to 
                law enforcement to combat wildlife trafficking at its source.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-red-200 text-sm">Intelligence operations in Mexico, China, and US</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-red-200 text-sm">Crime analysis and investigation reports</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-red-200 text-sm">Support for law enforcement agencies</span>
                </div>
              </div>

              <a 
                href="https://earthleagueinternational.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-block"
              >
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Learn About Operation Fake Gold
                </Button>
              </a>
            </div>

            {/* Gillnet Removal */}
            <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-600/30 rounded-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-green-300">Gillnet Removal</h3>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                Multi-institutional efforts in the Upper Gulf of California actively remove deadly gillnets 
                from vaquita habitat. Local fishers work alongside conservation organizations to clear dangerous nets.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-green-200 text-sm">Museo de la Ballena leading removal efforts</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-green-200 text-sm">Mexican Navy and Ministry collaboration</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-green-200 text-sm">Sea Shepherd and WWF partnership</span>
                </div>
              </div>

              <a 
                href="https://www.museodelaballena.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-block"
              >
                <Button className="bg-green-600 hover:bg-green-700 text-white">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Support Museo de la Ballena
                </Button>
              </a>
            </div>
          </div>

          {/* Project Esperanza Activities */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-8 mb-16">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-4">Project Esperanza: Scientific Monitoring</h3>
              <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
                These CIRVA-recommended and IWC-endorsed conservation initiatives provide critical data 
                and support for vaquita recovery efforts.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Photo Identification */}
              <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-blue-300 mb-3">Photo ID</h4>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Individual animal identification to understand life history and estimate minimum population.
                </p>
                <ul className="text-xs text-blue-200 space-y-1">
                  <li>• Survival rate analysis</li>
                  <li>• Population estimates</li>
                  <li>• Individual tracking</li>
                </ul>
              </div>

              {/* Acoustic Monitoring */}
              <div className="bg-purple-900/20 border border-purple-600/30 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-purple-300 mb-3">Acoustic Monitoring</h4>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Sound detection systems track vaquita presence and behavior patterns in their habitat.
                </p>
                <ul className="text-xs text-purple-200 space-y-1">
                  <li>• Vaquita click detection</li>
                  <li>• Habitat usage patterns</li>
                  <li>• Population distribution</li>
                </ul>
              </div>

              {/* Tissue Sampling */}
              <div className="bg-orange-900/20 border border-orange-600/30 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-orange-300 mb-3">Tissue Sampling</h4>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Live cell collection for San Diego Zoo's Frozen Zoo and genetic diversity studies.
                </p>
                <ul className="text-xs text-orange-200 space-y-1">
                  <li>• Frozen Zoo preservation</li>
                  <li>• DNA diversity analysis</li>
                  <li>• Future recovery options</li>
                </ul>
              </div>

              {/* Sustainable Development */}
              <div className="bg-teal-900/20 border border-teal-600/30 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-lg font-bold text-teal-300 mb-3">Community Support</h4>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Supporting local fishers and organizations implementing vaquita-safe alternatives.
                </p>
                <ul className="text-xs text-teal-200 space-y-1">
                  <li>• Fisher collaboration</li>
                  <li>• Alternative livelihoods</li>
                  <li>• Community engagement</li>
                </ul>
              </div>
            </div>
          </div>



          {/* What You Can Do (VaquitaCPR Style) */}
          <div className="mt-16">
            <h3 className="text-3xl font-bold text-white text-center mb-12">What You Can Do Right Now</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Use Your Voice */}
              <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-xl font-bold text-yellow-300 mb-4">Use Your Voice</h4>
                <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                  Sound an alarm heard round the world. Talk about vaquitas drowning, fishermen struggling, 
                  and the illegal fishery to blame. Share at dinner tables and water coolers.
                </p>
                <div className="space-y-3">
                  <Button 
                    onClick={() => handleShare('x')}
                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-white text-sm"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Post #vaquitacpr
                  </Button>
                  <p className="text-yellow-200 text-xs">
                    Follow and tag @FightforVaquita in your posts
                  </p>
                </div>
              </div>

              {/* Use Your Heart */}
              <div className="bg-pink-900/20 border border-pink-600/30 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-xl font-bold text-pink-300 mb-4">Use Your Heart</h4>
                <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                  Make a direct difference by supporting VaquitaCPR's Project Esperanza. Your contribution 
                  directly funds critical conservation efforts bringing vaquitas back from extinction.
                </p>
                <a 
                  href="https://www.vaquitacpr.org/donate/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-pink-600 hover:bg-pink-700 text-white text-sm">
                    <Heart className="h-4 w-4 mr-2" />
                    Donate Now
                  </Button>
                </a>
              </div>

              {/* Use Your Mind */}
              <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-xl font-bold text-blue-300 mb-4">Use Your Mind</h4>
                <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                  Choose sustainably-caught seafood. Talk to restaurants and grocers about sustainable fisheries. 
                  Increase demand for methods that protect oceans and ecosystems worldwide.
                </p>
                <a 
                  href="https://www.seafoodwatch.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Seafood Watch Guide
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Actions */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white text-center mb-16">Take It Further</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Educational Actions */}
            <div className="space-y-8">
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Educate Others</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4 py-2">
                    <h4 className="font-semibold text-blue-300 mb-2">In Schools & Universities</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Present about the vaquita crisis in biology, environmental science, or ethics classes. 
                      <a href="/resources" className="text-blue-400 hover:text-blue-300 ml-2">Download our presentation kit</a>
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-green-500 pl-4 py-2">
                    <h4 className="font-semibold text-green-300 mb-2">Community Events</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Organize awareness events at local libraries, environmental groups, or community centers. 
                      We can help you plan an event.
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-purple-500 pl-4 py-2">
                    <h4 className="font-semibold text-purple-300 mb-2">Online Content</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Create videos, blogs, or social media content about the vaquita crisis. 
                      Tag us and we'll help amplify your message.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                    <MessageCircle className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Contact Representatives</h3>
                </div>
                
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Political pressure has been crucial for vaquita protection in the past. Contact your government representatives 
                  to ask them to pressure Mexico for stronger enforcement.
                </p>
                
                <div className="space-y-3">
                  <a 
                    href="https://www.usa.gov/elected-officials"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Find Your Representatives
                    </Button>
                  </a>
                  <Button className="w-full border border-green-600 text-green-400 hover:bg-green-600 hover:text-white bg-transparent">
                    <FileText className="h-4 w-4 mr-2" />
                    Download Letter Template
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Column - Emergency Actions */}
            <div className="space-y-8">
              <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-red-300 mb-4">Emergency Actions</h3>
                <p className="text-red-200 mb-6 leading-relaxed">
                  When critical situations develop, we'll send emergency alerts for immediate actions 
                  that could save the remaining vaquitas.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <span className="text-red-200 text-sm">Petition signings during international meetings</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <span className="text-red-200 text-sm">Social media campaigns during critical moments</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <span className="text-red-200 text-sm">Contact drives to government officials</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-green-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-8">
            Every Action Matters
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            With fewer than 10 vaquitas left, there's no action too small. Whether you share one post, 
            sign one petition, or organize one event - you're part of the solution.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              onClick={() => handleShare('x')}
              className="bg-white text-blue-900 hover:bg-gray-100 font-bold px-10 py-4 text-xl rounded-lg"
            >
              <Share2 className="mr-3 h-6 w-6" />
              Start by Sharing
            </Button>
            <a href="mailto:contact@fightforvaquita.org" className="inline-block">
              <Button className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold px-10 py-4 text-xl rounded-lg bg-transparent">
                <Mail className="mr-3 h-6 w-6" />
                Get Involved
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Action;